import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Phone, MessageCircle, ShieldCheck, Plane, Hotel, Bus, FileCheck2,
  Languages, Briefcase, Calendar, Users, CheckCircle2, Lock, MapPin, Star, Quote,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { trackLead, trackEvent } from "@/lib/analytics";
import { normalizeIndianPhone, openWhatsApp } from "@/lib/lead-helpers";

const WHATSAPP_NUMBER = "919501030117";
const CANTON_PRICE_FROM = 79999;
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

// Canton Fair session dates (Phases vary; we show full window).
const SESSIONS = [
  { label: "Spring 2026", dates: "15 April – 5 May 2026", session: "Phase 1–3 · Guangzhou" },
  { label: "Autumn 2026", dates: "15 October – 4 November 2026", session: "Phase 1–3 · Guangzhou" },
  { label: "Spring 2027", dates: "15 April – 5 May 2027", session: "Phase 1–3 · Guangzhou" },
  { label: "Autumn 2027", dates: "15 October – 4 November 2027", session: "Phase 1–3 · Guangzhou" },
];

const INCLUSIONS = [
  { icon: Plane,      title: "Return Flights",        body: "Ex-Delhi / Mumbai / BLR" },
  { icon: FileCheck2, title: "China Business Visa",   body: "Full invitation letter assistance" },
  { icon: Hotel,      title: "4★ Guangzhou Hotels",   body: "Near Pazhou Complex / Metro" },
  { icon: Bus,        title: "Fairground Transfers",  body: "Daily AC shuttle + airport" },
  { icon: Languages,  title: "Mandarin Interpreter",  body: "On-demand at the fair booth" },
  { icon: Briefcase,  title: "Buyer Badge Support",   body: "Pre-registered fair entry" },
];

const WHY_US = [
  { title: "20+ Years Trade Travel", body: "Trusted by 1,200+ Indian importers, exporters, and SMEs since 2004." },
  { title: "End-to-End Visa",      body: "Invitation letter, embassy slot, biometrics — handled in-house." },
  { title: "On-Ground Support",    body: "Hindi/English-speaking coordinator in Guangzhou for the entire trip." },
  { title: "Customised Itinerary", body: "Add factory visits in Yiwu, Shenzhen or Foshan — we route it for you." },
];

const FAQS = [
  { q: "How much does the Canton Fair trip cost?",
    a: "Our Canton Fair package starts from ₹79,999 per person (twin share, ex-Delhi). Final pricing depends on session phase, hotel category, length of stay, and add-on factory visits (Yiwu, Shenzhen, Foshan). We send a fully customised quote within 30 minutes of your enquiry." },
  { q: "What dates are the next Canton Fair sessions?",
    a: "Spring session: 15 April – 5 May 2026 and 15 April – 5 May 2027. Autumn session: 15 October – 4 November 2026 and 15 October – 4 November 2027. Each session runs three 5-day phases at the Pazhou Complex, Guangzhou." },
  { q: "Do you arrange the China Business (M) visa?",
    a: "Yes. We provide the official Canton Fair invitation letter, full documentation review, embassy appointment booking, and visa filing assistance from our Delhi/Mumbai/Bengaluru offices." },
  { q: "Can you add factory visits to Yiwu or Shenzhen?",
    a: "Absolutely. We design custom buyer routes covering Yiwu (small commodities), Shenzhen (electronics), Foshan (furniture/lighting), Dongguan (hardware) — with interpreter, transport, and pre-booked supplier meetings." },
  { q: "Is a Mandarin interpreter included?",
    a: "An English/Mandarin interpreter at the fair is included for the standard package. Full-day private interpreters for factory visits can be added at ₹8,000/day." },
  { q: "How early should I book?",
    a: "Hotel inventory in Guangzhou during fair weeks sells out 90+ days in advance. We recommend booking at least 8 weeks before the session opens for best fares and hotel availability." },
];

// Anonymised past-buyer wins (used in the social-proof carousel below).
// Replace city/category with real client logos once user shares them; copy
// is intentionally light on names for GDPR/PII safety.
const BUYER_WINS = [
  {
    name: "R. Agarwal",
    city: "Ludhiana",
    category: "Hosiery & Apparel Importer",
    sessions: 6,
    quote: "Sixth Canton Fair with Vacations Pro. Visa, Pazhou hotel, interpreter — all stitched together. I just fly in and source.",
    win: "Saved 14 days of paperwork per trip",
  },
  {
    name: "V. Shah",
    city: "Surat",
    category: "Textile & Saree Buyer",
    sessions: 4,
    quote: "They routed me through Yiwu after the fair — closed 11 supplier deals in one trip. Worth every rupee.",
    win: "11 supplier deals in one trip",
  },
  {
    name: "K. Reddy",
    city: "Hyderabad",
    category: "Electronics & Gadgets Reseller",
    sessions: 3,
    quote: "First-time China visit. Their visa team handled the invitation letter in 36 hours. Stress-free start to finish.",
    win: "Visa cleared in 36 hours",
  },
  {
    name: "A. Mehta",
    city: "Mumbai",
    category: "Home Furnishing Wholesaler",
    sessions: 5,
    quote: "Hotel was 8 minutes from Pazhou gate. Interpreter was sharp on pricing — saved me ~12% on three POs.",
    win: "12% saved on supplier POs",
  },
  {
    name: "S. Khan",
    city: "Delhi NCR",
    category: "LED & Lighting Importer",
    sessions: 2,
    quote: "Custom Foshan factory route after the fair was a game-changer. Vacations Pro pre-booked all my meetings.",
    win: "9 Foshan factories in 2 days",
  },
];

export default function CantonFairLanding() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const conversionSent = useRef(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [session, setSession] = useState(SESSIONS[0].label);
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [winIdx, setWinIdx] = useState(0);

  // Auto-advance the buyer-wins carousel every 5s (pauses on focus/hover via CSS).
  useEffect(() => {
    const id = setInterval(() => setWinIdx((i) => (i + 1) % BUYER_WINS.length), 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("canton_lp_view", { campaign: "canton_fair_2026_27" });
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const submit = async (e) => {
    e?.preventDefault();
    if (hp) return;
    const cleanPhone = normalizeIndianPhone(phone);
    if (!name.trim() || !cleanPhone) { toast.error("Please share your name and phone number."); return; }
    if (cleanPhone.length !== 10) { toast.error("Enter a valid 10-digit Indian mobile number."); return; }
    setSubmitting(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: cleanPhone,
        destination: "Canton Fair Guangzhou",
        preferences: `Canton Fair custom package · Session: ${session}`,
        source: "lp_canton_fair",
        utm_source: utm.get("utm_source") || null,
        utm_medium: utm.get("utm_medium") || null,
        utm_campaign: utm.get("utm_campaign") || "canton_fair",
        utm_term: utm.get("utm_term") || null,
        utm_content: utm.get("utm_content") || null,
      };
      const { data: lead } = await api.post("/leads", payload);
      if (!conversionSent.current) {
        conversionSent.current = true;
        // Standard cross-channel lead conversion (GA4 + Meta + Google Ads)
        trackLead({
          source: "lp_canton_fair_form",
          destination: "Canton Fair Guangzhou",
          value: CANTON_PRICE_FROM,
          label: "canton_fair_lead",
          extra: { lead_id: lead?.id || null, session },
        });
        // Custom Meta Pixel conversion for this campaign
        try {
          if (typeof window.fbq === "function") {
            window.fbq("trackCustom", "CantonFairLead", {
              session,
              destination: "Canton Fair Guangzhou",
              value: CANTON_PRICE_FROM,
              currency: "INR",
            });
          }
        } catch (err) { console.warn("fbq custom failed", err); }
        // Custom GA4 event for the campaign
        try {
          if (typeof window.gtag === "function") {
            window.gtag("event", "canton_fair_lead", {
              session,
              campaign: "canton_fair_2026_27",
              value: CANTON_PRICE_FROM,
              currency: "INR",
            });
          }
        } catch (err) { console.warn("gtag custom failed", err); }
      }
      const lines = [
        "Hi Vacations Pro — please send me a custom Canton Fair package quote (starting ₹79,999/person).",
        "",
        `Name: ${payload.name}`,
        `Phone: +91 ${cleanPhone}`,
        `Email: ${payload.email || "—"}`,
        `Session: ${session}`,
      ];
      openWhatsApp(WHATSAPP_NUMBER, lines.join("\n"));
      toast.success("Got it! WhatsApp is opening — a Canton Fair specialist will call within 30 minutes.");
      setName(""); setEmail(""); setPhone("");
    } catch (err) {
      console.warn("Canton LP submit failed", err);
      toast.error(err?.response?.data?.detail || "Couldn't submit — please WhatsApp us directly.");
    } finally { setSubmitting(false); }
  };

  // SEO + structured data
  const eventSchema = {
    "@context": "https://schema.org", "@type": "Event",
    "name": "Canton Fair (China Import & Export Fair) — Spring & Autumn 2026 / 2027",
    "description": "Custom travel packages from India for the Canton Fair in Guangzhou. Flights, China business visa, hotel near Pazhou Complex, interpreter, and optional factory visits — from ₹79,999 per person.",
    "startDate": "2026-04-15",
    "endDate": "2027-11-04",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "China Import and Export Fair Complex (Pazhou)",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "382 Yuejiang Middle Road, Haizhu District",
        "addressLocality": "Guangzhou",
        "addressRegion": "Guangdong",
        "postalCode": "510335",
        "addressCountry": "CN",
      },
    },
    "image": ["https://vacationspro.com/assets/canton-hero.jpg"],
    "offers": {
      "@type": "Offer",
      "url": "https://vacationspro.com/canton-fair",
      "price": String(CANTON_PRICE_FROM),
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "validFrom": "2026-01-01",
    },
    "organizer": {
      "@type": "TravelAgency", "name": "Vacations Pro International Pvt. Ltd.",
      "url": "https://vacationspro.com", "telephone": "+91-9501030117",
    },
  };
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white text-[var(--vp-text)]" data-testid="canton-fair-page">
      <Helmet>
        <title>Canton Fair 2026 & 2027 Packages from India ₹79,999 | Vacations Pro</title>
        <meta name="description" content="Custom Canton Fair travel packages from India — Spring & Autumn 2026/2027 sessions. Includes return flights, China business visa, Guangzhou 4★ hotel, fairground transfers, Mandarin interpreter & buyer badge. From ₹79,999 per person." />
        <meta name="keywords" content="Canton Fair 2026, Canton Fair 2027, Canton Fair package from India, Guangzhou trade trip, China business visa, Yiwu factory visit, Pazhou complex hotel, India to Canton Fair, Canton Fair tour operator" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://vacationspro.com/canton-fair" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Canton Fair 2026 & 2027 Packages from India | From ₹79,999" />
        <meta property="og:description" content="Customised Canton Fair packages — flights + China business visa + Guangzhou hotel + interpreter + buyer badge. Spring & Autumn 2026/2027 sessions." />
        <meta property="og:url" content="https://vacationspro.com/canton-fair" />
        <meta property="og:image" content="https://vacationspro.com/assets/canton-hero.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Vacations Pro" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Canton Fair 2026 & 2027 Packages from India | From ₹79,999" />
        <meta name="twitter:description" content="Customised Canton Fair packages — flights + visa + hotel + interpreter. Spring & Autumn 2026/2027." />
        <meta name="twitter:image" content="https://vacationspro.com/assets/canton-hero.jpg" />

        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[var(--vp-border)] px-5 md:px-10 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex-shrink-0" data-testid="canton-logo">
          <img src="/logo-header.png?v=blue3" alt="Vacations Pro" className="h-12 md:h-16 w-auto object-contain" />
        </button>
        <div className="flex items-center gap-2">
          <a href={`tel:+${WHATSAPP_NUMBER}`}
             onClick={() => trackLead({ source: "lp_canton_call_header", destination: "Canton Fair", label: "canton_fair_lead" })}
             className="hidden sm:inline-flex items-center gap-2 bg-[var(--vp-primary)] text-white rounded-full px-4 py-2 text-xs font-semibold">
            <Phone className="w-3.5 h-3.5" /> +91 95010 30117
          </a>
          <button onClick={scrollToForm}
                  className="bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full px-4 py-2 text-xs font-semibold transition-colors"
                  data-testid="canton-header-cta">
            Get Custom Quote
          </button>
        </div>
      </header>

      {/* HERO with video */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            src="/assets/canton-fair.mp4"
            poster="/assets/canton-hero.jpg"
            autoPlay muted loop playsInline preload="metadata"
            data-testid="canton-hero-video"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        </div>

        <div className="relative z-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 px-6 md:px-12 lg:px-20 pt-12 md:pt-20 pb-14 md:pb-24 max-w-[1280px] mx-auto">
          <div className="text-white">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold">
                <Calendar className="w-3.5 h-3.5 text-[var(--vp-accent)]" /> Spring & Autumn · 2026 · 2027
              </div>
              <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 backdrop-blur border border-emerald-400/40 rounded-full px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold text-emerald-50">
                <Briefcase className="w-3.5 h-3.5" /> Buyer Programme
              </div>
            </div>
            <h1 className="font-serif-vp text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]">
              Canton Fair 2026 & 2027.<br />
              <span className="text-[var(--vp-accent)]">From {inr(CANTON_PRICE_FROM)} — Fully Customised.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/90 leading-relaxed max-w-[620px]">
              India&apos;s most trusted travel partner for the Canton Fair in Guangzhou. Return flights,
              China business visa, hotel near Pazhou Complex, fairground transfers, Mandarin interpreter
              and buyer-badge support — every Canton Fair package is custom-built around <strong>your</strong>
              sourcing goals.
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button onClick={scrollToForm}
                className="bg-[var(--vp-accent)] hover:bg-[var(--vp-accent)]/90 text-[#0f1512] rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-all hover:-translate-y-0.5"
                data-testid="canton-hero-cta">
                Get My Custom Quote
              </button>
              <div className="inline-flex items-center gap-1.5 text-[13px] text-white/85">
                <Star className="w-4 h-4 text-[var(--vp-accent)]" fill="currentColor" />
                4.8/5 · 1,200+ buyers served
              </div>
            </div>
            <div className="mt-7 grid grid-cols-4 gap-3 max-w-[520px]">
              {[
                ["20+", "Years"],
                ["1,200+", "Buyers"],
                ["48h", "Visa Help"],
                ["24×7", "On-Ground"],
              ].map(([big, small]) => (
                <div key={small} className="bg-white/10 backdrop-blur border border-white/15 rounded-lg px-2 py-2.5 text-center">
                  <div className="font-serif-vp text-lg text-white">{big}</div>
                  <div className="text-[10px] tracking-wider uppercase text-white/65 mt-0.5">{small}</div>
                </div>
              ))}
            </div>
          </div>

          {/* FORM */}
          <form ref={formRef} onSubmit={submit}
            className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-5 md:p-7 space-y-3"
            data-testid="canton-form">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--vp-secondary)]">
              Canton Fair · Custom Quote
            </div>
            <h2 className="font-serif-vp text-2xl md:text-3xl text-[var(--vp-primary)] leading-snug">
              Our Canton Fair specialist calls back in under 30 minutes.
            </h2>
            <input type="text" name="hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                   value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" />

            <BareInput label="Full Name" value={name} onChange={setName} required testid="canton-name" />
            <BareInput label="Email" type="email" value={email} onChange={setEmail} testid="canton-email" />
            <BareInput label="Phone (10-digit)" type="tel" required testid="canton-phone"
              value={phone} onChange={(v) => setPhone(normalizeIndianPhone(v))} />

            <div>
              <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold mb-2">Preferred Session</div>
              <div className="grid grid-cols-2 gap-2" data-testid="canton-sessions">
                {SESSIONS.map((s) => (
                  <button key={s.label} type="button" onClick={() => setSession(s.label)}
                    className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${
                      session === s.label
                        ? "bg-[var(--vp-primary)] text-white border-[var(--vp-primary)]"
                        : "bg-white border-[var(--vp-border)] text-[var(--vp-text)] hover:border-[var(--vp-primary)]"
                    }`}
                    data-testid={`canton-session-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                    <div className="font-semibold">{s.label}</div>
                    <div className={`text-[10px] mt-0.5 ${session === s.label ? "text-white/80" : "text-[var(--vp-muted)]"}`}>{s.dates}</div>
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-xl py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="canton-submit">
              <MessageCircle className="w-4 h-4" />
              {submitting ? "Sending…" : "Get My Custom Canton Fair Quote"}
            </button>
            <p className="text-[11px] text-[var(--vp-muted)] text-center mt-2 inline-flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" /> No spam · 100% confidential · Invitation letter included
            </p>
          </form>
        </div>
      </section>

      {/* INCLUSIONS */}
      <section className="px-6 md:px-12 lg:px-20 py-12 max-w-[1200px] mx-auto">
        <span className="vp-label text-[var(--vp-secondary)]">What&apos;s Included</span>
        <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
          Everything a serious Indian buyer needs in Guangzhou.
        </h2>
        <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="canton-inclusions">
          {INCLUSIONS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-[var(--vp-bg)] border border-[var(--vp-border)] rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-[var(--vp-primary)]/10 flex items-center justify-center mx-auto mb-3">
                <Icon className="w-5 h-5 text-[var(--vp-primary)]" strokeWidth={1.6} />
              </div>
              <div className="text-sm font-semibold text-[var(--vp-primary)]">{title}</div>
              <div className="text-[11px] text-[var(--vp-muted)] mt-0.5">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SESSIONS TIMELINE */}
      <section className="bg-[var(--vp-bg)] px-6 md:px-12 lg:px-20 py-16 border-y border-[var(--vp-border)]">
        <div className="max-w-[1200px] mx-auto">
          <span className="vp-label text-[var(--vp-secondary)]">Upcoming Sessions</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
            Canton Fair Spring & Autumn — 2026 and 2027.
          </h2>
          <p className="text-sm text-[var(--vp-muted)] mt-3 max-w-3xl">
            Each Canton Fair session runs three 5-day phases at the China Import &amp; Export Fair
            Complex (Pazhou), Guangzhou. We design departures from Delhi, Mumbai, Bengaluru and Kolkata
            for buyers across India.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="canton-sessions-timeline">
            {SESSIONS.map((s) => (
              <div key={s.label} className="bg-white border border-[var(--vp-border)] rounded-xl p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--vp-muted)]">{s.session}</div>
                <div className="font-serif-vp text-xl text-[var(--vp-primary)] mt-2">{s.label}</div>
                <div className="text-sm text-[var(--vp-text)] mt-1">{s.dates}</div>
                <button onClick={() => { setSession(s.label); scrollToForm(); }}
                  className="mt-4 text-xs font-semibold text-[var(--vp-secondary)] hover:underline"
                  data-testid={`canton-timeline-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                  Get a quote for {s.label} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-[1200px] mx-auto">
        <span className="vp-label text-[var(--vp-secondary)]">Why Vacations Pro</span>
        <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
          India&apos;s most-trusted Canton Fair travel partner.
        </h2>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_US.map((w) => (
            <div key={w.title} className="bg-white border border-[var(--vp-border)] rounded-xl p-5">
              <ShieldCheck className="w-5 h-5 text-[var(--vp-secondary)] mb-3" strokeWidth={1.6} />
              <div className="font-serif-vp text-lg text-[var(--vp-primary)]">{w.title}</div>
              <p className="text-xs text-[var(--vp-muted)] leading-relaxed mt-1.5">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PAST BUYER WINS — social proof carousel */}
      <section
        className="bg-gradient-to-b from-[var(--vp-primary)] to-[#0b1320] text-white px-6 md:px-12 lg:px-20 py-16"
        data-testid="canton-buyer-wins"
        onMouseEnter={() => { /* CSS pauses via aria-live, JS keeps simple */ }}
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
            <div>
              <span className="vp-label text-[var(--vp-accent)]">Past Buyer Wins</span>
              <h2 className="font-serif-vp text-3xl md:text-4xl leading-tight mt-3 max-w-3xl">
                Real results from Indian buyers who travelled with us.
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => setWinIdx((i) => (i - 1 + BUYER_WINS.length) % BUYER_WINS.length)}
                className="w-10 h-10 rounded-full border border-white/25 hover:bg-white/10 transition-colors inline-flex items-center justify-center"
                aria-label="Previous buyer story" data-testid="canton-wins-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setWinIdx((i) => (i + 1) % BUYER_WINS.length)}
                className="w-10 h-10 rounded-full border border-white/25 hover:bg-white/10 transition-colors inline-flex items-center justify-center"
                aria-label="Next buyer story" data-testid="canton-wins-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Active card */}
          <div className="mt-6 bg-white/[0.06] border border-white/15 backdrop-blur rounded-2xl p-7 md:p-10 relative overflow-hidden"
               aria-live="polite" data-testid="canton-wins-active-card">
            <Quote className="absolute top-5 right-6 w-16 h-16 text-white/10" strokeWidth={1.2} />
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
              <div>
                <p className="text-lg md:text-2xl font-serif-vp leading-snug text-white">
                  &ldquo;{BUYER_WINS[winIdx].quote}&rdquo;
                </p>
                <div className="mt-5 flex items-center gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-full bg-[var(--vp-accent)] text-[#0f1512] inline-flex items-center justify-center font-serif-vp text-lg font-bold">
                    {BUYER_WINS[winIdx].name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{BUYER_WINS[winIdx].name} · {BUYER_WINS[winIdx].city}</div>
                    <div className="text-[12px] text-white/65">{BUYER_WINS[winIdx].category}</div>
                  </div>
                </div>
              </div>
              <div className="bg-[var(--vp-accent)]/15 border border-[var(--vp-accent)]/35 rounded-xl px-5 py-4 md:min-w-[220px]">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--vp-accent)] font-semibold">Outcome</div>
                <div className="font-serif-vp text-xl mt-1 text-white leading-snug">{BUYER_WINS[winIdx].win}</div>
                <div className="text-[11px] text-white/60 mt-3">{BUYER_WINS[winIdx].sessions} Canton Fairs with us</div>
              </div>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="mt-5 flex items-center justify-center gap-2" data-testid="canton-wins-dots">
            {BUYER_WINS.map((w, i) => (
              <button
                key={w.name}
                type="button"
                onClick={() => setWinIdx(i)}
                aria-label={`Show buyer story ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === winIdx ? "w-8 bg-[var(--vp-accent)]" : "w-2 bg-white/30 hover:bg-white/55"}`}
                data-testid={`canton-wins-dot-${i}`}
              />
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="canton-wins-stats">
            {[
              ["1,200+", "Indian buyers travelled"],
              ["98%", "Visa success rate"],
              ["20+", "Years at the Canton Fair"],
              ["₹0", "Cancellation fee (60+ days)"],
            ].map(([big, small]) => (
              <div key={small} className="text-center bg-white/[0.04] border border-white/10 rounded-xl px-3 py-4">
                <div className="font-serif-vp text-2xl text-[var(--vp-accent)]">{big}</div>
                <div className="text-[11px] tracking-wider uppercase text-white/65 mt-1">{small}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="bg-[var(--vp-bg)] px-6 md:px-12 lg:px-20 py-16 border-y border-[var(--vp-border)]">
        <div className="max-w-[1200px] mx-auto">
          <span className="vp-label text-[var(--vp-secondary)]">Inside Pazhou</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
            The world&apos;s largest trade fair — engineered for you.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <img src="/assets/canton-hero.jpg" alt="Canton Fair Pazhou Complex Guangzhou — Vacations Pro Buyer Tour"
                 className="w-full rounded-xl object-cover aspect-[16/10]" />
            <img src="/assets/canton-fair-2.jpg" alt="Canton Fair buyer pavilion electronics Guangzhou"
                 className="w-full rounded-xl object-cover aspect-[16/10]" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="max-w-[820px] mx-auto">
          <span className="vp-label text-[var(--vp-secondary)]">Frequently Asked</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3">
            Canton Fair — everything you need to know.
          </h2>
          <div className="mt-8 divide-y divide-[var(--vp-border)] bg-white border border-[var(--vp-border)] rounded-xl"
               data-testid="canton-faq">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q}>
                  <button onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[var(--vp-bg)]/50 transition-colors"
                    data-testid={`canton-faq-${i}`} aria-expanded={isOpen}>
                    <span className="font-serif-vp text-lg text-[var(--vp-primary)] leading-snug">{f.q}</span>
                    <span className={`text-2xl text-[var(--vp-secondary)] shrink-0 transition-transform ${isOpen ? "rotate-45" : ""}`}>+</span>
                  </button>
                  {isOpen && <div className="px-5 pb-5 text-sm text-[var(--vp-text)] leading-relaxed">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--vp-primary)] text-white px-6 md:px-12 lg:px-20 py-16 text-center">
        <div className="max-w-[720px] mx-auto">
          <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold mb-5">
            <Users className="w-3.5 h-3.5 text-[var(--vp-accent)]" /> Limited group spots — Spring 2026
          </div>
          <h2 className="font-serif-vp text-3xl md:text-5xl leading-tight">
            Your Canton Fair trip — fully customised, from {inr(CANTON_PRICE_FROM)}.
          </h2>
          <p className="text-white/75 mt-4 text-sm md:text-base">
            Visa, flights, Guangzhou hotels, interpreter, factory routing — handled. Talk to a specialist now.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={scrollToForm}
              className="bg-[var(--vp-accent)] text-[#0f1512] hover:bg-[var(--vp-accent)]/90 rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors"
              data-testid="canton-final-form-cta">
              Get My Custom Quote
            </button>
            <a href={`tel:+${WHATSAPP_NUMBER}`}
               onClick={() => trackLead({ source: "lp_canton_final_call", destination: "Canton Fair", label: "canton_fair_lead" })}
               className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold inline-flex items-center gap-2 transition-colors"
               data-testid="canton-final-call">
              <Phone className="w-4 h-4" /> Call +91 95010 30117
            </a>
          </div>
          <p className="text-[12px] text-white/60 mt-5">
            <CheckCircle2 className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
            Invitation letter included · 100% confidential · Instant expert call-back
          </p>
        </div>
      </section>

      {/* Floating WhatsApp + mobile sticky bar */}
      <a href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent("Hello Vacations Pro — I want a custom Canton Fair package quote (from ₹79,999).")}`}
         target="_blank" rel="noopener noreferrer"
         onClick={() => trackLead({ source: "lp_canton_whatsapp_float", destination: "Canton Fair", label: "canton_fair_lead" })}
         className="fixed bottom-24 right-5 z-40 w-14 h-14 flex items-center justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full shadow-[0_12px_30px_rgba(37,211,102,0.45)] transition-all hover:-translate-y-0.5"
         aria-label="Chat with us on WhatsApp" data-testid="canton-whatsapp-float">
        <MessageCircle className="w-6 h-6" />
      </a>

      <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-white border-t border-[var(--vp-border)] shadow-[0_-8px_20px_rgba(0,0,0,0.08)] px-3 py-2.5 flex items-center gap-2">
        <a href={`tel:+${WHATSAPP_NUMBER}`}
           onClick={() => trackLead({ source: "lp_canton_mobile_call", destination: "Canton Fair", label: "canton_fair_lead" })}
           className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[var(--vp-primary)] text-[var(--vp-primary)] rounded-lg py-2.5 text-xs font-bold">
          <Phone className="w-3.5 h-3.5" /> Call Now
        </a>
        <button onClick={scrollToForm}
          className="flex-[1.4] bg-[#25D366] text-white rounded-lg py-2.5 text-xs font-bold inline-flex items-center justify-center gap-1.5"
          data-testid="canton-mobile-form-cta">
          <MessageCircle className="w-3.5 h-3.5" /> Custom Quote
        </button>
      </div>

      <footer className="px-6 md:px-12 py-8 pb-28 md:pb-8 text-center text-xs text-[var(--vp-muted)] flex items-center justify-center gap-2">
        <MapPin className="w-3.5 h-3.5" />
        K-10 Tower, Feroze Gandhi Market, Ludhiana 141001 · © {new Date().getFullYear()} Vacations Pro
      </footer>
    </div>
  );
}

function BareInput({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <label className="block border border-[var(--vp-border)] rounded-lg px-3 py-2 bg-white focus-within:border-[var(--vp-primary)] transition-colors">
      <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold">{label}</div>
      <input type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none text-[15px] mt-0.5 placeholder:text-[var(--vp-muted)]"
        data-testid={testid} />
    </label>
  );
}
